import { creatureModelMatrix } from "./creaturemesh";

/** Where a stone's smoke stands: `UpdateAttachingInstances` multiplies the
 *  bone's matrix into the actor's world matrix every frame
 *  (`ActorInstanceAttach.cpp:519-566`). A stone neither moves nor turns once
 *  placed, so its smoke bone is its bind-pose offset carried by the same model
 *  matrix `creatureModelMatrix` draws the stone's mesh with. */
export function stoneSmokeAnchor(
  xMm: number,
  yMm: number,
  zMm: number,
  headingSteps: number,
  offsetMm: readonly number[],
): { xMm: number; yMm: number; zMm: number } {
  const m = creatureModelMatrix(xMm, yMm, zMm, headingSteps);
  const [ox, oy, oz] = offsetMm;
  return {
    xMm: m[0] * ox + m[4] * oy + m[8] * oz + m[12],
    yMm: m[1] * ox + m[5] * oy + m[9] * oz + m[13],
    zMm: m[2] * ox + m[6] * oy + m[10] * oz + m[14],
  };
}

/** One stone species' smoke, as `tools/build_stone_smoke.py` writes it: the
 *  bone, the effect key of its first `SmokeFileName`, and the bone's bind
 *  offset in the model frame, in millimetres. */
export interface StoneSmokeRow {
  readonly bone: string;
  readonly effect: string;
  readonly offsetMm: readonly number[];
}

export const STONE_SMOKE_URL = "creaturemesh/smoke.json";

/** `smoke.json`'s rows by species name -- the name a stone's entity carries,
 *  which is what the painter looks its smoke up by -- or an empty table for a
 *  body that holds none. */
export function stoneSmokeTable(body: unknown): ReadonlyMap<string, StoneSmokeRow> {
  const stones = (body as { stones?: Record<string, StoneSmokeRow> } | null)?.stones;
  return new Map(Object.entries(stones ?? {}));
}

/** The table by species name, or an empty one when the file is absent or
 *  unreadable: a stone without its smoke is still drawn. */
export async function loadStoneSmoke(
  fetchJson: (url: string) => Promise<Response> = (url) => fetch(url),
): Promise<ReadonlyMap<string, StoneSmokeRow>> {
  try {
    const response = await fetchJson(STONE_SMOKE_URL);
    if (!response.ok) return new Map();
    return stoneSmokeTable(await response.json());
  } catch {
    return new Map();
  }
}
